import { useEffect, useRef, useState } from "react";

const CARDS = [
  {
    label: "EVIDENCE 01",
    big: "3x",
    claim: "Higher habit completion than diary logging",
    body: "In a 29-day clinic pilot, a one-tap Habit Tile held completion roughly 3x higher than diary-style logging.",
    source: "CLINIC PILOT · 29 DAYS",
  },
  {
    label: "EVIDENCE 02",
    big: "+72%",
    claim: "Increase in daily water intake",
    body: "In the TriSip field experiment, users drank 72% more water each day, and 53% held a seven-day streak.",
    source: "TRISIP FIELD EXPERIMENT",
  },
  {
    label: "EVIDENCE 03",
    big: "IIT-G",
    claim: "IIT Guwahati incubated",
    body: "Developed inside the Indian Institute of Technology Guwahati incubation ecosystem.",
    source: "INSTITUTIONAL",
  },
  {
    label: "EVIDENCE 04",
    big: "TOI",
    claim: "Featured in The Times of India",
    body:
      "\"Trifit adjusts to the user rather than the other way around.\" A profile of Shiv Pardal building a wellness system for real life.",
    source: "TIMES OF INDIA · FACES OF MODERN INDIA",
    href:
      "https://timesofindia.indiatimes.com/business/india-business/the-faces-of-modern-india-entrepreneurs-and-experts-driving-change/articleshow/132389115.cms",
  },
  {
    label: "EVIDENCE 05",
    big: "FORBES",
    claim: "Featured in Forbes India",
    body:
      "\"Technology that understands real life, fits naturally into it, and makes consistency easier with every interaction.\"",
    source: "FORBES INDIA · BUSINESS LEADERS",
    href:
      "https://www.forbesindia.com/article/upfront/brand-connect/meet-the-business-leaders-solving-real-world-problems-across-education-technology-wellness-fmcg-and-hospitality/2996822/1",
  },
];

const Card = ({ c, hidden }) => {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] tracking-[0.24em] text-ash">
          {c.label}
        </p>
        <div className="h-px w-12 bg-line" />
      </div>
      <p className="mt-5 font-display text-4xl font-extralight tracking-tight text-white md:mt-8 md:text-7xl">
        {c.big}
      </p>
      <p className="mt-3 font-display text-base font-light leading-snug text-white md:mt-4 md:text-xl">
        {c.claim}
      </p>
      <p className="mt-3 text-xs leading-relaxed text-fog md:mt-4 md:text-sm">
        {c.body}
      </p>
      <div className="mt-auto flex items-center justify-between gap-3 pt-5 md:pt-8">
        <p className="font-mono text-[9px] tracking-[0.22em] text-ash">
          {c.source}
        </p>
        {c.href && (
          <span className="font-mono text-[9px] tracking-[0.22em] text-signal transition-colors group-hover/card:text-white">
            READ →
          </span>
        )}
      </div>
    </>
  );

  const className =
    "group/card flex w-[76vw] max-w-[330px] shrink-0 flex-col rounded-xl border border-line bg-ink-2/50 p-5 md:w-[420px] md:max-w-none md:p-9 transition-colors duration-500";

  if (c.href) {
    return (
      <a
        href={c.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-hidden={hidden || undefined}
        data-testid={hidden ? undefined : `evidence-card-${c.label.slice(-2)}`}
        className={`${className} hover:border-signal/60`}
      >
        {inner}
      </a>
    );
  }
  return (
    <div
      aria-hidden={hidden || undefined}
      data-testid={hidden ? undefined : `evidence-card-${c.label.slice(-2)}`}
      className={className}
    >
      {inner}
    </div>
  );
};

const AUTO_PX_PER_SEC = 40; // slow drift

const Evidence = () => {
  const scrollRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const rafRef = useRef(null);
  const lastRef = useRef(0);

  // Auto-scroll loop (with reset when reaching end)
  useEffect(() => {
    if (paused) return;
    lastRef.current = performance.now();
    const tick = (now) => {
      const dt = (now - lastRef.current) / 1000;
      lastRef.current = now;
      const el = scrollRef.current;
      if (el && !paused) {
        el.scrollLeft += AUTO_PX_PER_SEC * dt;
        // reset to start after reaching the end
        if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 4) {
          el.scrollLeft = 0;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused]);

  // While hovered, convert vertical wheel to horizontal scroll and stop Lenis
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!paused) return;
      const dominantY = Math.abs(e.deltaY) > Math.abs(e.deltaX);
      if (dominantY) {
        e.preventDefault();
        e.stopPropagation();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [paused]);

  return (
    <section
      data-testid="evidence-section"
      className="relative overflow-hidden py-24 md:py-32"
    >
      <div className="px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-end gap-4">
          <p
            data-testid="evidence-hint"
            className="font-mono text-[9px] tracking-[0.22em] text-ash"
          >
            HOVER TO CONTROL · SCROLL HORIZONTALLY →
          </p>
        </div>
      </div>
      <div
        ref={scrollRef}
        data-testid="evidence-track"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        className="evidence-scroll mt-8 overflow-x-auto overflow-y-hidden md:mt-12"
        style={{ scrollbarWidth: "none" }}
      >
        <div className="flex w-max items-stretch gap-4 px-6 md:gap-8 md:px-12">
          {CARDS.map((c) => (
            <Card key={c.label} c={c} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Evidence;
